import { useState, useEffect } from "react";
import { Modal, Button, Input, TextArea, Select } from "../../../shared";
import {
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
} from "../../../redux/services/taskTemplates/api";
import { toast } from "react-toastify";
import { usePermissions } from "../../../hooks/usePermissions";

const TaskTemplateModal = ({
  editMode = false,
  template,
  visible,
  setVisible,
  onClose,
  onSubmit,
}) => {
  const { isSuperAdmin } = usePermissions();
  const canSeeIndustry = isSuperAdmin;
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    description: "",
    industry: "trucking",
    category: "general",
    appliesTo: "both",
    order: 0,
    required: false,
    requiresPhoto: false,
    requiresSignature: false,
    requiresNote: false,
    autoApply: true,
    active: true,
  });

  const [createTemplate, { isLoading: isCreating }] =
    useCreateTaskTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateTaskTemplateMutation();

  const isLoading = isCreating || isUpdating;

  // Initialize form data when template changes
  useEffect(() => {
    if (template && editMode) {
      setFormData({
        name: template.name || "",
        title: template.title || "",
        description: template.description || "",
        industry: template.industry || "trucking",
        category: template.category || "general",
        appliesTo: template.appliesTo || "both",
        order: template.order || 0,
        required: template.required || false,
        requiresPhoto: template.requiresPhoto || false,
        requiresSignature: template.requiresSignature || false,
        requiresNote: template.requiresNote || false,
        autoApply: template.autoApply !== false,
        active: template.active !== false,
      });
    } else if (template && !editMode) {
      // New template with pre-filled industry
      setFormData((prev) => ({
        ...prev,
        industry: template.industry || "trucking",
        name: "",
        title: "",
        description: "",
      }));
    }
  }, [template, editMode]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error("Template name is required");
        return;
      }
      if (!formData.title.trim()) {
        toast.error("Task title is required");
        return;
      }

      const payload = { ...formData };
      if (!canSeeIndustry) {
        delete payload.industry;
      }

      if (editMode) {
        await updateTemplate({ id: template._id, ...payload }).unwrap();
        toast.success("Template updated successfully");
      } else {
        await createTemplate(payload).unwrap();
        toast.success("Template created successfully");
      }

      onSubmit();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(
        error.data?.message || "An error occurred while saving the template"
      );
    }
  };

  const industryOptions = [
    { value: "trucking", label: "Trucking" },
    { value: "service_repair", label: "Service/Repair" },
    { value: "general", label: "General" },
  ];

  const categoryOptions = [
    { value: "pickup", label: "Pickup" },
    { value: "delivery", label: "Delivery" },
    { value: "documentation", label: "Documentation" },
    { value: "inspection", label: "Inspection" },
    { value: "safety", label: "Safety" },
    { value: "customer", label: "Customer" },
    { value: "general", label: "General" },
  ];

  const appliesToOptions = [
    { value: "both", label: "Jobs & Loads" },
    { value: "job", label: "Jobs Only" },
    { value: "leg", label: "Loads Only" },
  ];

  return (
    <Modal
      title={editMode ? "Edit Task Template" : "Create Task Template"}
      visible={visible}
      setVisible={setVisible}
      onClose={onClose}
      size="md"
      hideFooter
    >
      <div className="space-y-4">
        {/* Name & Title */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Template Name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.value)}
            placeholder="e.g., pickup_photos"
            required
          />
          <Input
            label="Task Title"
            value={formData.title}
            onChange={(e) => handleChange("title", e.value)}
            placeholder="e.g., Take pickup photos"
            required
          />
        </div>

        {/* Description */}
        <TextArea
          label="Description"
          value={formData.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Brief description of this task..."
          rows={2}
        />

        {/* Industry (superAdmin only), Category, Applies To */}
        <div className="grid grid-cols-3 gap-4">
          {!canSeeIndustry ? null : (
            <Select
              label="Industry"
              value={formData.industry}
              onChange={(e) => handleChange("industry", e.value)}
              options={industryOptions}
            />
          )}
          <Select
            label="Category"
            value={formData.category}
            onChange={(e) => handleChange("category", e.value)}
            options={categoryOptions}
          />
          <Select
            label="Applies To"
            value={formData.appliesTo}
            onChange={(e) => handleChange("appliesTo", e.value)}
            options={appliesToOptions}
          />
        </div>

        {/* Order */}
        <Input
          label="Sort Order"
          type="number"
          value={formData.order}
          onChange={(e) => handleChange("order", parseInt(e.value) || 0)}
          placeholder="0"
        />

        {/* Checkboxes */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Task Requirements
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => handleChange("required", e.target.checked)}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Required task</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requiresPhoto}
                onChange={(e) =>
                  handleChange("requiresPhoto", e.target.checked)
                }
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Requires photo</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requiresSignature}
                onChange={(e) =>
                  handleChange("requiresSignature", e.target.checked)
                }
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Requires signature</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requiresNote}
                onChange={(e) => handleChange("requiresNote", e.target.checked)}
                className="h-4 w-4 text-primary-600 rounded"
              />
              <span className="text-sm text-gray-700">Requires note</span>
            </label>
          </div>
        </div>

        {/* Auto-apply & Active */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.autoApply}
              onChange={(e) => handleChange("autoApply", e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded"
            />
            <span className="text-sm text-gray-700">
              Auto-apply to new jobs/loads
            </span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.active}
              onChange={(e) => handleChange("active", e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded"
            />
            <span className="text-sm text-gray-700">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="alternative" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isLoading}>
            {editMode ? "Update Template" : "Create Template"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TaskTemplateModal;
